import React, { useState, useEffect } from 'react';
import { authFetch } from '../lib/authFetch';
import { Users, TrendingUp, TrendingDown, DollarSign, Award, Search, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const fmt = (v) => {
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v?.toFixed(0) ?? 0}`;
};
const pct = (v) => `${((v ?? 0) * 100).toFixed(2)}%`;
const color = (v) => (v >= 0 ? 'text-emerald-600' : 'text-red-500');

// Determine investor type based on name
const getInvestorType = (name, rawType) => {
  if (!name) return rawType || 'RETAIL';
  
  const nameLower = name.toLowerCase();
  
  // Check for SMSFs
  if (nameLower.includes('smsf') || nameLower.includes('super fund') || 
      nameLower.includes('superannuation') || nameLower.includes('retirement')) {
    return 'SMSF';
  }
  
  // Check for company/institutional names
  const companyKeywords = ['fund', 'advisory', 'capital', 'investments', 'partners', 
    'holdings', 'group', 'pty', 'ltd', 'limited', 'corporation', 'corp', 
    'management', 'wealth', 'trust', 'asset', 'qsuper', 'australian'];
  
  if (companyKeywords.some(kw => nameLower.includes(kw))) {
    return 'WHOLESALE';
  }
  
  // If it looks like a person's name (First Last format), it's retail
  const words = name.trim().split(' ');
  if (words.length >= 2 && words[0][0] === words[0][0].toUpperCase()) {
    // Check if it's not a company name pattern
    if (!companyKeywords.some(kw => nameLower.includes(kw))) {
      return 'RETAIL';
    }
  }
  
  return rawType === 'INSTITUTIONAL' ? 'WHOLESALE' : (rawType || 'RETAIL');
};

const INVESTOR_TYPE_COLORS = {
  RETAIL: 'bg-sky-100 text-sky-700',
  WHOLESALE: 'bg-purple-100 text-purple-700',
  SMSF: 'bg-amber-100 text-amber-700',
};

const PORTFOLIO_COLORS = {
  bedrock: 'bg-emerald-100 text-emerald-700',
  fortress: 'bg-sky-100 text-sky-700',
  horizon: 'bg-indigo-100 text-indigo-700',
  velocity: 'bg-amber-100 text-amber-700',
  summit: 'bg-orange-100 text-orange-700',
  nebula: 'bg-purple-100 text-purple-700',
};

export default function BookOfRecordPage() {
  const [stats, setStats] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [allotments, setAllotments] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedPortfolio, setSelectedPortfolio] = useState('all');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [statsRes, holdingsRes, allotRes, redRes] = await Promise.all([
          authFetch(`${API_BASE}/api/lms/book-of-record/holdings/stats`),
          authFetch(`${API_BASE}/api/lms/book-of-record/holdings?limit=500`),
          authFetch(`${API_BASE}/api/lms/book-of-record/allotments?limit=100`),
          authFetch(`${API_BASE}/api/lms/book-of-record/redemptions?limit=100`),
        ]);
        if (statsRes.ok) setStats(await statsRes.json());
        if (holdingsRes.ok) setHoldings(await holdingsRes.json());
        if (allotRes.ok) setAllotments(await allotRes.json());
        if (redRes.ok) setRedemptions(await redRes.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const totalAUM = stats.reduce((s, x) => s + (x.total_aum ?? 0), 0);
  const totalInvestors = stats.reduce((s, x) => s + (x.total_investors ?? 0), 0);

  const filtered = holdings.filter(h => {
    const matchPt = selectedPortfolio === 'all' || h.portfolio_type === selectedPortfolio;
    const matchSearch = !search || h.investor_name?.toLowerCase().includes(search.toLowerCase())
      || h.investor_id?.toLowerCase().includes(search.toLowerCase());
    return matchPt && matchSearch;
  });

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-gray-900">Book of Record</h1>
          <Badge className="bg-sky-100 text-sky-700">Fundbase</Badge>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Customer unit holdings — managed externally by Fundbase Registry
        </p>
        <p className="text-xs text-gray-400 mt-1">
          This is the authoritative record of who owns units in each portfolio
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total AUM</p>
            <p className="text-2xl font-bold text-gray-900">{fmt(totalAUM)}</p>
            <p className="text-xs text-gray-400 mt-1">Across all portfolios</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Investors</p>
            <p className="text-2xl font-bold text-gray-900">{totalInvestors}</p>
            <p className="text-xs text-gray-400 mt-1">Active wholesale investors</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Allotments (30d)</p>
            <p className="text-2xl font-bold text-emerald-600">{allotments.length}</p>
            <p className="text-xs text-gray-400 mt-1">Unit issuances via Fundbase</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Redemptions (30d)</p>
            <p className="text-2xl font-bold text-orange-600">{redemptions.length}</p>
            <p className="text-xs text-gray-400 mt-1">No illiquid fund sales</p>
          </CardContent>
        </Card>
      </div>

      {/* Portfolio breakdown cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {stats.map(s => (
          <Card key={s.portfolio_type}
            className={`cursor-pointer border-2 transition-all ${selectedPortfolio === s.portfolio_type ? 'border-indigo-500' : 'border-transparent'}`}
            onClick={() => setSelectedPortfolio(selectedPortfolio === s.portfolio_type ? 'all' : s.portfolio_type)}>
            <CardContent className="pt-3 pb-3">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PORTFOLIO_COLORS[s.portfolio_type] || 'bg-gray-100 text-gray-700'}`}>
                {s.portfolio_type}
              </span>
              <p className="text-lg font-bold mt-1">{fmt(s.total_aum)}</p>
              <p className="text-xs text-gray-500">{s.total_investors} investors</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="holdings">
        <TabsList>
          <TabsTrigger value="holdings">Holdings ({filtered.length})</TabsTrigger>
          <TabsTrigger value="allotments">Allotments</TabsTrigger>
          <TabsTrigger value="redemptions">Redemptions</TabsTrigger>
        </TabsList>

        {/* Holdings Table */}
        <TabsContent value="holdings">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    className="pl-9 pr-4 py-2 w-full border rounded-md text-sm"
                    placeholder="Search investor name or ID..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>
                {selectedPortfolio !== 'all' && (
                  <Badge variant="outline" className="cursor-pointer" onClick={() => setSelectedPortfolio('all')}>
                    {selectedPortfolio} ×
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Investor</TableHead>
                    <TableHead>Portfolio</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Units Held</TableHead>
                    <TableHead className="text-right">Avg Entry</TableHead>
                    <TableHead className="text-right">Current Value</TableHead>
                    <TableHead className="text-right">Return</TableHead>
                    <TableHead>Fundbase Ref</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.slice(0, 50).map((h, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm text-gray-900">{h.investor_name}</p>
                          <p className="text-xs text-gray-400">{h.investor_id}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PORTFOLIO_COLORS[h.portfolio_type] || 'bg-gray-100 text-gray-700'}`}>
                          {h.portfolio_type}
                        </span>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const invType = getInvestorType(h.investor_name, h.investor_type);
                          return (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${INVESTOR_TYPE_COLORS[invType] || 'bg-gray-100 text-gray-700'}`}>
                              {invType}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600 whitespace-nowrap" data-testid={`investor-joined-${i}`}>
                        {h.first_investment_date
                          ? new Date(h.first_investment_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
                          : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {(h.units_held ?? 0).toLocaleString('en-AU', { maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        ${(h.avg_entry_price ?? 0).toFixed(4)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {fmt(h.current_value ?? 0)}
                      </TableCell>
                      <TableCell className={`text-right text-sm font-medium ${color(h.total_return_pct ?? 0)}`}>
                        {(h.total_return_pct ?? 0) >= 0 ? '+' : ''}{(h.total_return_pct ?? 0).toFixed(2)}%
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-gray-400 font-mono">{h.fundbase_investor_id}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filtered.length > 50 && (
                <p className="text-center text-xs text-gray-400 py-3">
                  Showing 50 of {filtered.length} investors
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Allotments Table */}
        <TabsContent value="allotments">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Investor</TableHead>
                    <TableHead>Portfolio</TableHead>
                    <TableHead className="text-right">Cash In</TableHead>
                    <TableHead className="text-right">App Price</TableHead>
                    <TableHead className="text-right">Units Allotted</TableHead>
                    <TableHead>Fundbase Ref</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allotments.filter(a => selectedPortfolio === 'all' || a.portfolio_type === selectedPortfolio).map((a, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs text-gray-500">
                        {new Date(a.allotment_date).toLocaleDateString('en-AU')}
                      </TableCell>
                      <TableCell className="text-sm">{a.investor_id}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PORTFOLIO_COLORS[a.portfolio_type] || 'bg-gray-100 text-gray-700'}`}>
                          {a.portfolio_type}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium">{fmt(a.cash_amount)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">${(a.application_price ?? 0).toFixed(4)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {(a.units_allotted ?? 0).toLocaleString('en-AU', { maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-xs text-gray-400 font-mono">{a.fundbase_allotment_ref}</TableCell>
                      <TableCell>
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">{a.status}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Redemptions Table */}
        <TabsContent value="redemptions">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Investor</TableHead>
                    <TableHead>Portfolio</TableHead>
                    <TableHead className="text-right">Cash Paid</TableHead>
                    <TableHead className="text-right">Red. Price</TableHead>
                    <TableHead className="text-right">Units Redeemed</TableHead>
                    <TableHead>Funded By</TableHead>
                    <TableHead>Fundbase Ref</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {redemptions.filter(r => selectedPortfolio === 'all' || r.portfolio_type === selectedPortfolio).map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs text-gray-500">
                        {new Date(r.request_date).toLocaleDateString('en-AU')}
                      </TableCell>
                      <TableCell className="text-sm">{r.investor_id}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PORTFOLIO_COLORS[r.portfolio_type] || 'bg-gray-100 text-gray-700'}`}>
                          {r.portfolio_type}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium">{fmt(r.cash_paid)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">${(r.redemption_price ?? 0).toFixed(4)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {(r.units_redeemed ?? 0).toLocaleString('en-AU', { maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell>
                        <Badge className={r.funded_by === 'CROSSING' ? 'bg-sky-100 text-sky-700 text-xs' : 'bg-amber-100 text-amber-700 text-xs'}>
                          {r.funded_by}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-400 font-mono">{r.fundbase_redemption_ref}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
